import { Space } from '@fullstory/proto/fs/services/dx-objects';
import React, { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { useLocalStorage, useSessionStorage } from '@fullstory/react-hooks';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';
import { QueryFrom, SPACE_ROUTE } from '@fullstory/routes';

interface ActiveSpace {
  space?: Space;
  tab?: QueryFrom<typeof SPACE_ROUTE>['tab'];
  /**
   * When a single section is the basis for Create workflows.
   */
  sectionId?: string;
}
interface ActiveSpaceValue {
  activeSpace?: ActiveSpace;
  persistedActiveSpace?: ActiveSpace;
  setActiveSpace: (activeSpace?: ActiveSpace) => void;
}

export const ActiveSpaceContext = React.createContext<ActiveSpaceValue>({
  activeSpace: undefined,
  persistedActiveSpace: undefined,
  setActiveSpace: () => {},
});

export const ActiveSpaceProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const { email } = useSelector(fullstorySelectors.getUserInfo);
  const activeSpaceKey = useMemo(() => `${email}-${orgId}-activeSpace`, [email, orgId]);
  // use session storage for the active Space so it doesn't jump around between tabs
  const [activeSpace, setActiveSpace] = useSessionStorage<ActiveSpace | undefined>(
    activeSpaceKey,
    undefined,
  );
  // use local storage to persist the active Space
  // this should should have one use, and that is redirecting at app startup time
  const landingSpaceKey = `${email}-${orgId}-landing-space`;
  const [persistedActiveSpace, setPersistedActiveSpace] = useLocalStorage<ActiveSpace | undefined>(
    landingSpaceKey,
    undefined,
  );

  useEffect(() => {
    // capture active space changes into the landing space for new FSTA app instances
    // this works in sync with the active space value
    if (activeSpace?.space) {
      setPersistedActiveSpace(activeSpace);
    }
  }, [activeSpace, setPersistedActiveSpace]);

  const values = useMemo(
    () => ({
      activeSpace,
      persistedActiveSpace,
      setActiveSpace,
    }),
    [activeSpace, persistedActiveSpace, setActiveSpace],
  );

  return <ActiveSpaceContext.Provider value={values}>{children}</ActiveSpaceContext.Provider>;
};

export const useActiveSpaceContext = () => useContext(ActiveSpaceContext);
